import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import { Form, Col, Row, Container } from 'react-bootstrap';
import { ThemeProvider, createTheme } from '@material-ui/core';
import InputTheme from '../../Common/Helpers/InputTheme';
import PropTypes from 'prop-types';
import { customValidators, object, mixed, string, date } from '../../../Helpers/Validators/customValidators';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { sub } from 'date-fns';
import States from '../../../Helpers/Lists/States';
import StatesWithDisabled from '../../../Helpers/Lists/StatesWithDisabled';
import { InputMaskHeight, InputMaskWeight, InputMaskDate } from '../../../Helpers/Formatters';
import { CheckboxInput, TextInput, SelectInput, StyledRadioInput } from '../../../Helpers/Inputs';
import userIcon from '../../../../icons/user.svg';
import { getBasicInfo, setBasicInfo } from '../../../../sessionStorage';
import { calculateBMI } from '../../../Helpers/Conversion';
import './AboutMe.scss';
import { SvgIcon } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import { withStyles } from '@material-ui/core/styles';

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    fontSize: 14,
    boxShadow: theme.shadows[1],
  },
}))(Tooltip);

export default function AboutMe(props) {

  const { register, errors, handleSubmit, setValue, triggerValidation } = useForm({
    validationSchema: AboutMe.ValidationSchema(props.ageSpecs),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const [birthdate, setBirthdate] = useState(props.birthdate);

  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    setValue(field, value);
    props.onFieldChange(field, value);
    triggerValidation(field);
    if (field === 'birthdate') {
      setBirthdate(value);
    }
  };

  useEffect(() => {
    // manually register fields for validation that don't play nice with react-hook-form register
    register({ name: 'state' });
    setValue('state', props.formState.state);
    register({ name: 'gender' });
    setValue('gender', props.formState.gender);
    register({ name: 'birthdate' });
    setValue('birthdate', props.formState.birthdate);
    register({ name: 'height' });
    setValue('height', props.formState.height);
    register({ name: 'weight' });
    setValue('weight', props.formState.weight);
    register({ name: 'usResident' });
    setValue('usResident', props.formState.usResident);
    register({ name: 'bmi' });
    setValue('bmi', props.formState.bmi);
    register({ name: 'tobacco' });
    setValue('tobacco', props.formState.tobacco);
  }, [register]);

  function calculateTotalHeight() {
    const radix = 10;
    var heightArray = props.formState.height.split(" ")
    var heightFeet = parseInt(heightArray[0], radix);
    var heightInches;
    for (var i = 1; i < heightArray.length; i++) {
      if (heightArray[i] !== "" && heightArray[i] !== "ft") {
        heightInches = parseInt(heightArray[i], radix);
        break;
      }
    };
    return heightFeet * 12 + heightInches;
  }

  useEffect(() => {
    if (props.formState.height && props.formState.weight) {
      var weight = props.formState.weight.split(" ")[0];
      var totalHeightInches = calculateTotalHeight();
      handleValueChange("bmi", calculateBMI(totalHeightInches, weight));
    }
  }, [props.formState.height, props.formState.weight])

  const onSubmit = () => {

    const totalHeight = calculateTotalHeight();
    var weight = props.formState.weight.split(" ")[0];

    if (props.validateBMI != null && props.validateBMI(totalHeight, props.formState.bmi, weight) && props.onDeclined != null) {
      props.onDeclined();
    } else {
      // Save formState to basicinfo
      var basicInfoFromSessionStorage = getBasicInfo();
      basicInfoFromSessionStorage.gender = props.formState.gender;
      basicInfoFromSessionStorage.birthdate = props.formState.birthdate;
      basicInfoFromSessionStorage.state = props.formState.state;
      basicInfoFromSessionStorage.height = props.formState.height;
      basicInfoFromSessionStorage.weight = props.formState.weight;
      basicInfoFromSessionStorage.tobacco = props.formState.tobacco;
      basicInfoFromSessionStorage.usResident = props.formState.usResident;
      basicInfoFromSessionStorage.bmi = props.formState.bmi;

      setBasicInfo(basicInfoFromSessionStorage);
      props.onNext();
    }
  }
  const [tooltip, setTooltip] = useState(false);
  const handleTooltipAboutClose = () => {
    setTooltip(false);
  };
  const handleTooltipAboutOpen = () => {
    setTooltip(true);
  };

  return (
    <ThemeProvider theme={(theme) => createTheme({
      ...theme,
      ...InputTheme(),
    })}>
      <Container>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="justify-content-center" >
            <Col lg={4}>
              <Row className="justify-content-center" >
                <SvgIcon color='primary' component={userIcon} className="icons" fontSize="large" />
              </Row>
              <Row className="justify-content-center" >
                <h3 className="py-3">Profile</h3>
                <Tooltip title="This information should represent the person who will be insured and covered by the policy, whether that's you or someone else." id="about-tooltip" aria-describedby="about-tooltip" aria-label="more about sort by price" className="toolInfo" role="tooltip" >
                  <LightTooltip enterTouchDelay={0} leaveTouchDelay={5000}
                    onClose={() => handleTooltipAboutClose()}
                    open={tooltip}>
                    <button type='button' id="about" role="tooltip" className="quote-tooltip" onMouseOver={() => handleTooltipAboutOpen()} onClick={() => handleTooltipAboutOpen()} tabIndex="0" aria-disabled="true" aria-describedby="about"><InfoIcon color="action"></InfoIcon></button>
                  </LightTooltip>
                </Tooltip>
              </Row>
              <Form.Group>
                <StyledRadioInput
                  name="gender"
                  id="gender"
                  value={props.formState.gender}
                  options={[{ value: "F", label: "Female" }, { value: "M", label: "Male" }]}
                  onChange={e => handleValueChange('gender', e)}
                  errors={errors.gender}
                  isBlankIcon={true}
                />
              </Form.Group>

              <Form.Group>
                <TextInput
                  name="birthdate"
                  id="birthdate"
                  label="Date of Birth"
                  value={birthdate}
                  errors={errors && errors.birthdate}
                  inputRef={register}
                  onChange={(e) => handleValueChange('birthdate', e.target.value)}
                  InputProps={{
                    inputComponent: InputMaskDate,
                  }}
                />
              </Form.Group>

              <Form.Group>
                <SelectInput
                  name="state"
                  id="state"
                  value={props.formState.state}
                  onChange={(e) => handleValueChange('state', e.target.value)}
                  label="State"
                  inputRef={register}
                  selectOptions={props.unavailableStates ? StatesWithDisabled(props.unavailableStates) : States()}
                  errors={errors && errors.state}
                />
              </Form.Group>

              <Row>
                <Col>
                  <Form.Group>
                    <TextInput
                      name="height"
                      id="height"
                      label="Height"
                      value={props.formState.height}
                      errors={errors && errors.height}
                      inputRef={register}
                      onChange={(e) => handleValueChange('height', e.target.value)}
                      InputProps={{
                        inputComponent: InputMaskHeight,
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col>
                  <Form.Group>
                    <TextInput
                      name="weight"
                      id="weight"
                      label="Weight"
                      value={props.formState.weight}
                      errors={errors && errors.weight}
                      inputRef={register}
                      onChange={(e) => handleValueChange('weight', e.target.value)}
                      InputProps={{
                        inputComponent: InputMaskWeight,
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group>
                <SelectInput
                  name="tobacco"
                  id="tobacco"
                  value={props.formState.tobacco}
                  onChange={(e) => props.onFieldChange('tobacco', e.target.value)}
                  errors={errors.tobacco}
                  label="Tobacco/Nicotine Use"
                  hideEmptyOption
                  selectOptions={[
                    { value: 'Within the last year', label: 'Within the last year' },
                    { value: 'Within the last 1 to 3 years', label: 'Within the last 1 to 3 years' },
                    { value: 'Within the last 3 to 5 years', label: 'Within the last 3 to 5 years' },
                    { value: 'Over 5 years ago', label: 'Over 5 years ago' },
                    { value: 'Never', label: 'Never' },
                  ]}
                />
              </Form.Group>

            </Col>
          </Row>
          <Row className="mt-4">
            <Col className="text-right pb-4 profile-nav-container">
              <Button variant="contained" size="medium" color="primary" type="submit" className="px-4">
                NEXT
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>
    </ThemeProvider>
  );
}

AboutMe.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  hideBack: PropTypes.bool,
  ageSpecs: PropTypes.object.isRequired
};

AboutMe.ValidationSchema = (ageSpecs) => {
  return object().shape({
    gender: string().required('Gender is required.'),
    birthdate: date()
      .typeError('Invalid date.')
      .max(sub(new Date(), { years: ageSpecs.min, days: ageSpecs.minAgeDays || 0 }), `Minimum age is ${ageSpecs.minAgeDays ? ageSpecs.minAgeDays + ' days' : ageSpecs.min}.`)
      .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
      .required('Birthdate is required.'),
    state: string().required('State is required.'),
    height: customValidators.string()
      .required('Height is required.')
      .isValidHeightString('Invalid height', 3),
    weight: customValidators.string()
      .required('Weight is required.')
      .isValidWeightString('Please verify your weight has been entered correctly.', 78, 395),
    tobacco: string().required('This field is required.'),
  })
};

AboutMe.DefaultFormState = (prefill = false, basicInfo = null) => {
  return {
    gender: prefill ? 'M' : (basicInfo && basicInfo.gender) || '',
    birthdate: prefill ? '11/15/1980' : (basicInfo && basicInfo.birthdate) || '',
    fakeBirthdate: prefill ? '' : (basicInfo && basicInfo.fakeBirthdate) || '',
    tobacco: prefill ? 'Never' : (basicInfo && basicInfo.tobacco) || 'Never',
    state: prefill ? 'ID' : (basicInfo && basicInfo.state) || '',
    height: prefill ? '5 ft 10 in' : (basicInfo && basicInfo.height) || '',
    weight: prefill ? '175 pounds' : (basicInfo && basicInfo.weight) || '',
    usResident: true,
    bmi: (basicInfo && basicInfo.bmi) || undefined,
  };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/MCT/steps/AboutMe.js